import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s, tableTokens } from '@/theme/tokens';
import { Box, Stack, Text } from '@/ui';
import { formatDate } from '@/ui/formatters/formatDate';
import { CursorTooltip, ErrorOutline } from '@/ui/primitives';
import { CheckCircleOutlineIcon } from '@/ui/primitives/icons';
import { validationIconCursorTooltipSlotProps } from './projectCsvMappingSectionShared';
import { projectSettingsValidationPreviewSurfaceSx } from './projectSettingsSurfaceSx';
import { validationMappingValuesTitleSx } from './ValidationMappingValues.styles';
import type { ReactElement } from 'react';

type ValidationFilePreviewSectionProps = {
  fileName: string | null;
  headers: string[];
  rows: string[][];
  isMatchedHeader: (header: string) => boolean;
  resolveHeaderMatchLabel: (header: string) => string | null;
  isStatusColumn: (columnIndex: number) => boolean;
  isDurationColumn: (columnIndex: number) => boolean;
  isTimestampColumn: (columnIndex: number) => boolean;
  isMatchedStatusCell: (value: string, columnIndex: number) => boolean;
  resolveMatchedStatusLabel: (value: string, columnIndex: number) => string | null;
  isValidDurationCell: (value: string, columnIndex: number) => boolean;
  isValidTimestampCell: (value: string, columnIndex: number) => boolean;
};

type ValidationAdornmentState = {
  colorToken: 'error' | 'success' | 'textPrimary' | 'textSecondary';
  tooltip: string | null;
};

const formatFullDuration = (value: string): string => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return value;

  const totalMs = Math.max(0, Math.floor(parsedValue));
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const milliseconds = totalMs % 1000;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  if (milliseconds > 0) parts.push(`${milliseconds}ms`);

  return parts.length > 0 ? parts.join(' ') : '0ms';
};

const formatTooltipTimestamp = (value: string): string => {
  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) return value;
  return formatDate(parsedValue, { includeTime: true }) || value;
};

const buildInitialColumnWidths = (headers: string[], rows: string[][]): number[] =>
  headers.map((header, index) => {
    const longestCellLength = rows.reduce((maxLength, row) => {
      const cellValue = row[index] ?? '';
      return Math.max(maxLength, cellValue.length);
    }, 0);
    const estimatedWidth = Math.max(header.length, longestCellLength) * 6 + 24;
    return Math.max(72, Math.min(estimatedWidth, 200));
  });

const buildMinimumColumnWidths = (headers: string[]): number[] =>
  headers.map((header) => Math.max(64, Math.min(112, header.length * 5 + 28)));

const previewViewportFitBuffer = 16;

const normaliseColumnWidths = (
  widths: number[],
  minimumWidths: number[],
  availableWidth: number,
): number[] => {
  if (widths.length === 0 || availableWidth <= 0) return widths;

  const nextWidths = [...widths];
  let totalWidth = nextWidths.reduce((sum, width) => sum + width, 0);
  if (totalWidth <= availableWidth) return nextWidths;

  let remainingOverflow = totalWidth - availableWidth;
  while (remainingOverflow > 0) {
    const shrinkableColumns = nextWidths
      .map((width, index) => ({
        index,
        shrinkableWidth: width - (minimumWidths[index] ?? 64),
      }))
      .filter((column) => column.shrinkableWidth > 0)
      .sort((left, right) => right.shrinkableWidth - left.shrinkableWidth);

    if (shrinkableColumns.length === 0) {
      break;
    }

    shrinkableColumns.forEach((column) => {
      if (remainingOverflow <= 0) {
        return;
      }
      const nextWidth = nextWidths[column.index];
      const maximumReduction = nextWidth - (minimumWidths[column.index] ?? 64);
      const reduction = Math.min(
        maximumReduction,
        Math.max(1, Math.ceil(remainingOverflow / shrinkableColumns.length)),
      );
      nextWidths[column.index] = nextWidth - reduction;
      remainingOverflow -= reduction;
    });
  }

  totalWidth = nextWidths.reduce((sum, width) => sum + width, 0);
  return totalWidth > availableWidth ? nextWidths : nextWidths.map((width) => Math.floor(width));
};

export const ValidationFilePreviewSection = ({
  fileName,
  headers,
  rows,
  isMatchedHeader,
  resolveHeaderMatchLabel,
  isStatusColumn,
  isDurationColumn,
  isTimestampColumn,
  isMatchedStatusCell,
  resolveMatchedStatusLabel,
  isValidDurationCell,
  isValidTimestampCell,
}: ValidationFilePreviewSectionProps): ReactElement | null => {
  const tableViewportRef = useRef<HTMLDivElement | null>(null);
  const initialColumnWidths = useMemo(
    () => buildInitialColumnWidths(headers, rows),
    [headers, rows],
  );
  const minimumColumnWidths = useMemo(() => buildMinimumColumnWidths(headers), [headers]);
  const [columnWidths, setColumnWidths] = useState<number[]>(initialColumnWidths);
  const resizeStateRef = useRef<{
    columnIndex: number;
    startX: number;
    startWidth: number;
  } | null>(null);

  useEffect(() => {
    const viewportWidth = tableViewportRef.current?.clientWidth ?? 0;
    const availableWidth = Math.max(0, viewportWidth - previewViewportFitBuffer);
    setColumnWidths(
      normaliseColumnWidths(initialColumnWidths, minimumColumnWidths, availableWidth),
    );
  }, [initialColumnWidths, minimumColumnWidths]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent): void => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      const nextWidth = Math.max(
        100,
        Math.min(480, resizeState.startWidth + (event.clientX - resizeState.startX)),
      );
      setColumnWidths((currentWidths) =>
        currentWidths.map((width, index) =>
          index === resizeState.columnIndex ? nextWidth : width,
        ),
      );
    };

    const handleMouseUp = (): void => {
      resizeStateRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleResizeStart = (
    event: ReactMouseEvent<HTMLSpanElement>,
    columnIndex: number,
  ): void => {
    event.preventDefault();
    resizeStateRef.current = {
      columnIndex,
      startX: event.clientX,
      startWidth: columnWidths[columnIndex] ?? 140,
    };
  };

  const getHeaderValidationState = (
    header: string,
  ): {
    colorToken: 'success' | 'textPrimary';
    tooltip: string | null;
  } => {
    if (!isMatchedHeader(header)) {
      return { colorToken: 'textPrimary', tooltip: null };
    }

    return {
      colorToken: 'success',
      tooltip: `Header matches '${resolveHeaderMatchLabel(header) ?? 'field'}'`,
    };
  };

  const renderValidationAdornment = (
    validationState: ValidationAdornmentState | null,
  ): ReactElement | null => {
    if (validationState?.tooltip == null) {
      return null;
    }

    const icon =
      validationState.colorToken === 'error' ? (
        <ErrorOutline fontSize="small" sx={{ fontSize: 16 }} />
      ) : (
        <CheckCircleOutlineIcon fontSize="small" sx={{ fontSize: 16 }} />
      );

    return (
      <CursorTooltip
        title={validationState.tooltip}
        placement="right-start"
        arrow
        slotProps={validationIconCursorTooltipSlotProps}
      >
        <Box
          component="span"
          sx={(theme) => ({
            display: 'inline-flex',
            flexShrink: 0,
            color: resolveSemanticColor(theme, validationState.colorToken),
          })}
        >
          {icon}
        </Box>
      </CursorTooltip>
    );
  };

  const getCellValidationState = (
    value: string,
    columnIndex: number,
  ): {
    colorToken: 'error' | 'success' | 'textSecondary';
    tooltip: string | null;
  } | null => {
    if (value.length === 0) return null;

    if (isStatusColumn(columnIndex)) {
      return isMatchedStatusCell(value, columnIndex)
        ? {
            colorToken: 'success',
            tooltip: `Status matches '${resolveMatchedStatusLabel(value, columnIndex) ?? value}'`,
          }
        : {
            colorToken: 'error',
            tooltip: 'Status does not match any mapped status',
          };
    }

    if (isDurationColumn(columnIndex)) {
      return isValidDurationCell(value, columnIndex)
        ? {
            colorToken: 'success',
            tooltip: `Duration is valid: ${formatFullDuration(value)}`,
          }
        : {
            colorToken: 'error',
            tooltip: 'Duration is invalid',
          };
    }

    if (isTimestampColumn(columnIndex)) {
      return isValidTimestampCell(value, columnIndex)
        ? {
            colorToken: 'success',
            tooltip: `Timestamp is valid: ${formatTooltipTimestamp(value)}`,
          }
        : {
            colorToken: 'error',
            tooltip: 'Timestamp is invalid',
          };
    }

    return { colorToken: 'textSecondary', tooltip: null };
  };

  if (headers.length === 0) return null;

  return (
    <Stack gap="sm">
      <Stack gap="none">
        <Text role="body" sx={validationMappingValuesTitleSx}>
          File
        </Text>
        {fileName ? (
          <Text role="body" color="textSecondary">
            {fileName}
          </Text>
        ) : null}
      </Stack>
      <Box sx={projectSettingsValidationPreviewSurfaceSx}>
        <Box
          ref={tableViewportRef}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: 0,
              tableLayout: 'fixed',
            }}
          >
            <Box component="colgroup">
              {headers.map((_, index) => (
                <Box
                  key={`preview-col-${index}`}
                  component="col"
                  sx={{ width: `${columnWidths[index] ?? 140}px` }}
                />
              ))}
            </Box>
            <Box component="thead">
              <Box component="tr">
                {headers.map((header, index) => {
                  const headerValidationState = getHeaderValidationState(header);
                  const headerLabel = header || `Column ${index + 1}`;

                  return (
                    <Box
                      key={`preview-header-${index}`}
                      component="th"
                      sx={[
                        {
                          px: s('md'),
                          py: s('md'),
                          minHeight: `${tableTokens.headerHeight}px`,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          position: 'relative',
                        },
                        ...(index < headers.length - 1
                          ? [
                              {
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: s('lg'),
                                  bottom: s('lg'),
                                  right: 0,
                                  width: '1px',
                                  backgroundColor: 'divider',
                                  pointerEvents: 'none',
                                },
                              },
                            ]
                          : []),
                      ]}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          maxWidth: '100%',
                          minWidth: 0,
                          gap: s('sm'),
                          verticalAlign: 'top',
                          alignItems: 'center',
                        }}
                      >
                        <Text
                          role="body"
                          component="span"
                          sx={(theme) => ({
                            display: 'inline-block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: theme.typography.fontWeightMedium,
                            color: resolveSemanticColor(theme, 'textPrimary'),
                          })}
                        >
                          {headerLabel}
                        </Text>
                        {renderValidationAdornment(headerValidationState)}
                      </Box>
                      <Box
                        component="span"
                        role="presentation"
                        onMouseDown={(event: ReactMouseEvent<HTMLSpanElement>) => {
                          handleResizeStart(event, index);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: s('xs'),
                          height: '100%',
                          cursor: 'col-resize',
                          userSelect: 'none',
                          backgroundColor: 'transparent',
                          zIndex: 1,
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((row, rowIndex) => (
                <Box key={`preview-row-${rowIndex}`} component="tr">
                  {headers.map((_, columnIndex) => {
                    const cellValue = row[columnIndex] || '-';
                    const rawCellValue = row[columnIndex] || '';
                    const cellValidationState = getCellValidationState(rawCellValue, columnIndex);

                    return (
                      <Box
                        key={`preview-cell-${rowIndex}-${columnIndex}`}
                        component="td"
                        sx={{
                          px: s('md'),
                          py: s('sm'),
                          borderBottom: rowIndex < rows.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          verticalAlign: 'top',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            maxWidth: '100%',
                            minWidth: 0,
                            gap: s('sm'),
                            verticalAlign: 'top',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            role="body"
                            component="span"
                            sx={(theme) => ({
                              display: 'inline-block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: resolveSemanticColor(theme, 'textSecondary'),
                            })}
                          >
                            {cellValue}
                          </Text>
                          {renderValidationAdornment(cellValidationState)}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
